import React from 'react';
import { Chip, SxProps, Theme } from '@mui/material';
import { Archive as ArchiveIcon } from '@phosphor-icons/react/dist/ssr/Archive';
import { ArrowBendUpLeft as ArrowBendUpLeftIcon } from '@phosphor-icons/react/dist/ssr/ArrowBendUpLeft';
import { CheckCircle as CheckCircleIcon } from '@phosphor-icons/react/dist/ssr/CheckCircle';
import { Clock as ClockIcon } from '@phosphor-icons/react/dist/ssr/Clock';
import { Download } from '@phosphor-icons/react/dist/ssr/Download';
import { Gift } from '@phosphor-icons/react/dist/ssr/Gift';
import { HandCoins } from '@phosphor-icons/react/dist/ssr/HandCoins';
import { Minus as MinusIcon } from '@phosphor-icons/react/dist/ssr/Minus';
import { TrashIcon } from '@phosphor-icons/react/dist/ssr/Trash';
import { Trophy } from '@phosphor-icons/react/dist/ssr/Trophy';
import { X as XIcon } from '@phosphor-icons/react/dist/ssr/X';

function capitalizeString(input: string): string {
  if (!input) return '';

  const modifiedInput = input.replace(/_/g, ' ');

  return modifiedInput.charAt(0).toUpperCase() + modifiedInput.slice(1).toLowerCase();
}

export function StatusChip({ status }: { status: string }): React.JSX.Element {
  let styles: SxProps<Theme> | undefined = {};
  let icon: React.JSX.Element | undefined;

  switch (status) {
    case 'CONFIRMED':
    case 'APPROVED':
    case 'VERIFIED':
    case 'WON':
    case 'ACTIVE':
    case 'ENABLED':
    case 'CUSTOMER':
    case 'NEW':
    case 'LATEST':
    case 'PAYMENT':
    case 'SUCCEEDED':
    case 'UNLOCK':
    case 'RUNNING':
      styles = {
        color: 'var(--mui-palette-text-primary)',
      };
      icon = <CheckCircleIcon color="var(--mui-palette-success-main)" weight="fill" />;
      break;

    case 'OPEN':
    case 'PENDING':
    case 'MAINTENANCE':
    case 'NOTSTARTED':
    case 'PENDINGREVALIDATION':
    case 'BET_PLACED':
      // styles = {
      //   border: `1px solid ${california[400]}`,
      //   '.MuiTypography-root': { color: california[400] },
      //   background: 'none',
      // };
      icon = <ClockIcon color="var(--mui-palette-warning-main)" weight="fill" />;
      break;

    case 'VIP':
    case 'PROD':
      styles = {
        border: `1px solid`,
        borderColor: 'warning.main',
        bgcolor: 'warning.main',
        '.MuiTypography-root': {
          color: '#009DA5',
        },
      };
      break;

    case 'CANCELED':
    case 'TIMEDOUT':
    case 'DEPRECATE':
    case 'BLOCKED':
    case 'REVOKED':
    case 'INACTIVE':
    case 'LOCKED':
    case 'STOPPED':
    case 'CLOSED':
    case 'EXPIRED':
      icon = <MinusIcon color="var(--mui-palette-error-main)" />;
      break;

    case 'DELETED':
      styles = {
        border: `1px solid`,
        borderColor: 'warning.main',
        color: 'var(--mui-palette-text-primary)',
      };
      icon = <TrashIcon color="var(--mui-palette-error-main)" />;
      break;

    case 'REJECTED':
    case 'LOST':
    case 'DISABLED':
      styles = {
        color: 'var(--mui-palette-error-main)',
      };
      icon = <XIcon weight="fill" color="var(--mui-palette-error-main)" />;
      break;

    case 'NOT_SUBMITTED':
      styles = {
        border: `1px solid var(--mui-palette-divider)`,
        background: 'none',
        '.MuiTypography-root': { color: '#731428' },
      };
      break;

    case 'VISIBLE':
    case 'CASH':
    case 'AGENT':
    case 'DONE':
    case 'TOPUP':
      styles = {
        border: `1px solid var(--mui-palette-divider)`,
        background: 'none',
      };
      break;

    case 'CASHBACK':
    case 'MANUAL_BONUS':
      styles = {
        border: `1px solid var(--mui-palette-divider)`,
      };
      icon = <HandCoins weight="fill" color="var(--mui-palette-info-dark)" />;
      break;

    case 'AFF_PROFIT':
      styles = {
        border: `1px solid var(--mui-palette-divider)`,
      };
      icon = <Download weight="fill" color="var(--mui-palette-success-main)" />;
      break;

    case 'TOURNAMENT_PRIZE':
      styles = {
        border: `1px solid var(--mui-palette-divider)`,
      };
      icon = <Trophy weight="fill" color="var(--mui-palette-success-main)" />;
      break;

    case 'AUTH':
      styles = {
        border: `1px solid var(--mui-palette-divider)`,

        '.MuiTypography-root': { color: '#581aba' },
        background: 'none',
      };
      break;

    case 'DEPOSIT_BONUS':
    case 'TRANSACTIONS':
      styles = {
        border: `1px solid`,
        borderColor: 'success.200',

        '.MuiTypography-root': { color: 'success.200' },
        background: 'none',
      };
      break;

    case 'BET_SETTLED':
      styles = {
        border: `1px solid`,
        borderColor: 'info.100',

        '.MuiTypography-root': { color: 'info.100' },
        background: 'none',
      };
      break;

    // case 'WITHDRAW':
    case 'INVISIBLE':
    case 'HIDDEN':
    case 'REGISTRATION_BONUS':
    case 'OPERATORS':
      styles = {
        border: `1px solid`,
        borderColor: 'error.200',
        background: 'none',
        '.MuiTypography-root': { color: 'error.200' },
      };
      break;

    case 'INVOICE':
      styles = {
        border: `1px solid`,
        borderColor: 'warning.300',
        bgcolor: 'warning.300',
      };
      break;

    case 'BONUS':
      styles = {
        border: `1px solid var(--mui-palette-divider)`,
      };
      icon = <Gift weight="fill" color="var(--mui-palette-warning-main)" />;
      break;
    case 'BONUS_OUTLINED':
      styles = {
        border: `1px solid var(--mui-palette-divider)`,
        background: 'none',
        '.MuiTypography-root': { color: '#731428' },
      };
      break;

    case 'REPLIED':
      icon = <ArrowBendUpLeftIcon color="var(--mui-palette-success-main)" weight="fill" />;
      break;
    case 'ARCHIVED':
      icon = <ArchiveIcon color="var(--mui-palette-success-dark)" weight="fill" />;
      break;

    case 'PLAYERS':
      styles = {
        border: `1px solid var(--mui-palette-divider)`,
        background: 'none',
        '.MuiTypography-root': { color: '#7F56D9' },
      };
      break;
    case 'CMS':
      styles = {
        border: `1px solid var(--mui-palette-divider)`,
        background: 'none',
        '.MuiTypography-root': { color: 'warning.200' },
      };
      break;
    case 'SUPPORT':
      styles = {
        border: `1px solid var(--mui-palette-divider)`,
        background: 'none',
        '.MuiTypography-root': { color: '#009DA5' },
      };
      break;

    default:
      styles = {
        border: `1px solid var(--mui-palette-divider)`,
        background: 'none',
        '.MuiTypography-root': { color: 'info.200' },
      };
      break;
  }

  if (!status || status === '' || status === 'undefined') return <></>;

  return (
    <Chip
      icon={icon}
      label={capitalizeString(status)}
      size="small"
      variant="outlined"
      sx={{ px: '4px', py: '3px', ...styles }}
    />
  );
}
